import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/common/components/Button/Button';
import { Icon } from '@/common/components/Icons/Icon';
import { Modal } from '@/common/components/Modal/Modal';
import { RadioButton } from '@/common/components/RadioButton/RadioButton';
import { Stack } from '@/common/components/Stack/Stack';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { useUpdateEmployeeDetails } from '@/common/integrations/menhir/employee/employee/employee.hooks';
import { LanguageType } from '@/common/types/general';
import { notify } from '@/common/utils/toast/toast';

export function LanguageModal({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState<LanguageType>(
    i18n.language as LanguageType
  );
  const { employee, updateEmployee, company } = useEmployeeDetails();
  const { mutateAsync: updateEmployeeDetails, isLoading } =
    useUpdateEmployeeDetails();

  const onConfirmLanguage = async (
    language: LanguageType,
    closeModal: () => void
  ) => {
    try {
      const updatedEmployee = await updateEmployeeDetails({
        employeeId: employee.id,
        payload: {
          language,
        },
      });
      updateEmployee(updatedEmployee);
      notify({
        type: 'success',
        message: t('dashboard.settings.notification', { language }),
      });
      closeModal();
    } catch {
      notify({
        type: 'error',
        message: t('errors.api.server'),
      });
    }
  };

  return (
    <Modal
      title={t('dashboard.settings.language_modal.title')}
      icon={<Icon icon="language" />}
      iconBadgeColor="accentSecondary20"
      onClose={onClose}
      footer={({ close }) => (
        <Stack horizontal gap="md" justify="center">
          <Button variant="secondary" onPress={close} loading={isLoading}>
            {t('general.navigation.cancel')}
          </Button>
          <Button
            onPress={() => onConfirmLanguage(language, close)}
            loading={isLoading}
            className="flex-1"
          >
            {t('general.action.apply')}
          </Button>
        </Stack>
      )}
    >
      <RadioButton.Group
        variant="card"
        value={language}
        onChange={(value) => setLanguage(value as LanguageType)}
      >
        {company.market?.languages.map((language) => (
          <RadioButton.Option
            key={language}
            label={`${t(`dashboard.settings.language.${language}`)} (${language})`}
            value={language}
          />
        ))}
      </RadioButton.Group>
    </Modal>
  );
}
